import {
  Bell,
  Sparkles,
  BookOpen,
  CircleCheckBig,
  Inbox,
  LayoutDashboard,
  Menu,
  Settings,
  Smartphone,
  X,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Suspense, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { videoWorkshopService, authService, hasSalesProduct } from "../lib/auth";
import { formatDate } from "../lib/format";
import { adaptivePolling } from "../lib/polling";
import brandLogo from "../assets/brand/zhifangtang-logo.png";

import { AccountMenu } from "./AccountMenu";
import { OpenPhoneWallButton } from "./PhoneWorkspace";
import { useToast } from "./useToast";
import { LoadingState } from "./Feedback";
import { PageErrorBoundary } from "./PageErrorBoundary";

const navigation = [
  { path: "/overview", label: "运行总览", icon: LayoutDashboard },
  { path: "/task-settings", label: "项目设置", icon: CircleCheckBig },
  { path: "/blogger-intelligence", label: "行业情报", icon: BookOpen },
  { path: "/fangtang", label: "Agent获客", icon: Sparkles },
  { path: "/queue", label: "任务队列", icon: Smartphone },
  { path: "/replies", label: "客户消息", icon: Inbox },
  { path: "/leads", label: "线索库", icon: BookOpen },
  { path: "/settings", label: "运行配置", icon: Settings },
];

export function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname, location.hash]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const account = useQuery({queryKey:["auth-status"],queryFn:authService.status,enabled:false});
  const salesEnabled = hasSalesProduct(account.data?.user);
  const toast = useToast();
  const notificationRef = useRef<HTMLDivElement>(null);
  const videoEnabled = account.data?.reason === '' && account.data?.user?.entitlement === 'active' && account.data?.user?.products?.video === true;
  useEffect(() => {
    if (!videoEnabled) {
      void queryClient.cancelQueries({queryKey: ['video-workshop-entry']});
      queryClient.removeQueries({queryKey: ['video-workshop-entry']});
    }
  }, [videoEnabled, queryClient]);
  const video = useQuery({ queryKey: ["video-workshop-entry", account.data?.user?.id], queryFn: ({signal}) => videoWorkshopService.status(signal),
    enabled: videoEnabled, retry: false, refetchInterval: adaptivePolling(30_000, 60_000), refetchOnWindowFocus: "always" });
  const current =
    location.pathname === "/fangtang"
      ? { path: "/fangtang", label: "Agent获客", icon: Sparkles }
      : location.pathname === "/queue/new"
      ? { path: location.pathname, label: "精准安排任务", icon: CircleCheckBig }
      : location.pathname.startsWith("/tasks")
          ? { path: location.pathname, label: "任务执行", icon: CircleCheckBig }
          : location.pathname === "/account" ? { path: "/account", label: "我的账号", icon: Settings }
          : location.pathname === "/video-workshop" ? { path: "/video-workshop", label: "视频车间", icon: Sparkles }
          : navigation.find((item) => location.pathname === item.path
            || location.pathname.startsWith(item.path + "/"));

  const notifications = useQuery({
    queryKey: ["notifications"],
    queryFn: api.notifications,
    enabled: salesEnabled,
    refetchInterval: adaptivePolling(15_000),
  });
  const readMutation = useMutation({
    mutationFn: api.readNotification,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const dismissMutation = useMutation({
    mutationFn: api.dismissNotification,
    onSuccess: async () => {
      await Promise.all(["notifications", "health", "device-queues"].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })));
      toast.showToast("已忽略提醒，任务状态未改变");
    },
    onError: (error) => toast.showToast(error instanceof Error ? error.message : "忽略失败，请重试", "error"),
  });

  useEffect(() => {
    setSidebarOpen(false);
    setNotificationsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node)
      )
        setNotificationsOpen(false);
    };
    const keyHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNotificationsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, []);

  const openNotification = (id: string, targetPath: string | null) => {
    readMutation.mutate(id);
    setNotificationsOpen(false);
    if (targetPath) navigate(targetPath);
  };

  const attentionCount = notifications.data?.attention_count ?? 0;
  const attentionTone = attentionCount > 0
    ? notifications.data?.items.some((item) => item.level === "error") ? "danger" : "warning"
    : "normal";
  const attentionLabel = notifications.isError ? "待处理事项，读取失败"
    : notifications.isPending ? "待处理事项，读取中" : `待处理事项，${attentionCount}项`;

  return (
    <div className="app-shell">
      <aside className={`sidebar ${sidebarOpen ? "sidebar--open" : ""}`}>
        <div className="sidebar__brand">
          <span>
            <span className="sidebar__logo">
              <img src={brandLogo} alt="智方糖" />
            </span>
            <strong>智方糖AI获客</strong>
          </span>
          <button
            type="button"
            aria-label="关闭导航"
            onClick={() => setSidebarOpen(false)}
          >
            <X />
          </button>
        </div>
        <nav aria-label="主导航">
          {salesEnabled && <details className="nav-group" open><summary>AI获客</summary><div>
          {navigation.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `nav-item ${isActive ? "nav-item--active" : ""}`
              }
            >
              <Icon />
              <span>{label}</span>
            </NavLink>
          ))}
          </div></details>}
          {videoEnabled && video.data?.enabled && video.data.ready && video.data.entryUrl && !video.isError ? <details className="nav-group" open><summary>视频车间</summary><div>
            <NavLink to="/video-workshop" onClick={() => setSidebarOpen(false)}
              className={({isActive}) => `nav-item ${isActive ? "nav-item--active" : ""}`}>
              <Sparkles /><span>进入视频车间</span>
            </NavLink>
          </div></details> : null}
        </nav>
        <div className="sidebar__footer">
          {salesEnabled ? <NavLink to="/account#feedback">反馈问题</NavLink> : null}
          <AccountMenu />
        </div>
      </aside>
      {sidebarOpen ? (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="关闭导航"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}
      <div className="app-main">
        <header className="topbar">
          <div className="topbar__title">
            <button
              className="icon-button topbar__menu"
              type="button"
              aria-label="打开导航"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu />
            </button>
            <h1>{current?.label ?? "智方糖AI获客"}</h1>
          </div>
          <div className="topbar__actions">
            {salesEnabled && ["/overview", "/queue"].includes(location.pathname) ? <OpenPhoneWallButton /> : null}
            {salesEnabled && <div className="notification-wrap" ref={notificationRef}>
              <button
                className={`icon-button notification-button notification-button--${notifications.isError ? "normal" : attentionTone}`}
                type="button"
                aria-label={attentionLabel}
                title={attentionLabel}
                aria-expanded={notificationsOpen}
                aria-controls="notification-panel"
                onClick={() => setNotificationsOpen((value) => !value)}
              >
                <Bell />
                <span className="notification-button__label">待处理</span>
                <span key={attentionCount} className={`notification-button__count${attentionCount > 0 && !notifications.isError ? " notification-button__count--new" : ""}`}>
                  {notifications.isError ? "!" : notifications.isPending ? "…" : attentionCount > 99 ? "99+" : attentionCount}
                </span>
              </button>
              {notificationsOpen ? (
                <section
                  id="notification-panel"
                  className="notification-panel"
                  aria-label="通知列表"
                >
                  <header>
                    <span>
                      <strong>待处理事项</strong>
                      <small>
                        {notifications.isError ? "待处理数量暂不可用" : notifications.isPending ? "正在读取待处理事项" : notifications.data?.notifications_enabled === false ? '界面提醒已关闭，事项记录仍保留' : attentionCount
                          ? `${attentionCount}项待处理提醒`
                          : "当前没有待处理提醒"}
                      </small>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setNotificationsOpen(false);
                        navigate("/overview");
                      }}
                    >
                      查看总览
                    </button>
                  </header>
                  <div>
                    {notifications.isError ? (
                      <p className="notification-empty">
                        待处理事项读取失败，请打开手机运行总览查看状态。
                      </p>
                    ) : notifications.data?.items.length ? (
                      notifications.data.items.map((item) => (
                        <div className="notification-row" key={item.id}>
                        <button
                          type="button"
                          className={`notification-item ${item.read ? "notification-item--read" : ""}`}
                          onClick={() =>
                            openNotification(item.id, item.target_path)
                          }
                        >
                          <span
                            className={`notification-dot notification-dot--${item.level}`}
                          />
                          <span>
                            <strong>
                              {item.title}
                              {item.code !== "device_offline" && (item.group_count ?? 1) > 1
                                ? ` · 重复发生${item.group_count}次`
                                : ""}
                            </strong>
                            <small>{item.message}</small>
                            <time>{formatDate(item.created_at)}</time>
                          </span>
                        </button>
                        <button type="button" className="notification-dismiss"
                          aria-label={`忽略：${item.title}，${item.message}`}
                          disabled={dismissMutation.isPending}
                          onClick={() => dismissMutation.mutate(item.id)}>忽略</button>
                        </div>
                      ))
                    ) : (
                      <p className="notification-empty">没有待处理通知</p>
                    )}
                  </div>
                  <p className="notification-hint">忽略仅关闭本次提醒，不改变任务状态；问题恢复后再次发生会重新提醒。</p>
                </section>
              ) : null}
            </div>}
          </div>
        </header>
        <main className="page-content">
          <PageErrorBoundary key={location.pathname}>
            <Suspense fallback={<LoadingState label="正在加载页面" />}>
              <Outlet />
            </Suspense>
          </PageErrorBoundary>
        </main>
      </div>
    </div>
  );
}
